import type { ReactNode } from 'react'
import { House } from 'lucide-react'
import { Link, Navigate } from 'react-router'
import { AuthLoadingState, hasPermission, useAuth } from '@/features/auth'
// Import direct (pas le barrel `@/features/settings`), comme AppShell : évite le cycle passant par SettingsPage.
import { useModuleVisibilityList } from '@/features/settings/application/moduleVisibilityQueries'
import { resolveHomePath } from './homeDestination'

/**
 * Garde de `/vue-ensemble`, accueil par défaut de l'application (Vue d'ensemble). Désactivée dans Paramètres ›
 * Modules, l'adresse mène au premier accueil actif et autorisé (`resolveHomePath`), qui n'est jamais elle-même :
 * aucune boucle. S'il n'y en a aucun, la page « Aucun accueil disponible » le dit au lieu de rediriger.
 * Même repli ouvert que `RequireModule` : seule une désactivation confirmée par la donnée ferme la page.
 */
export function RequireHomeModule({ moduleKey, children }: { moduleKey: string; children: ReactNode }) {
  const { session } = useAuth()
  const query = useModuleVisibilityList()
  if (query.isPending) return <AuthLoadingState />
  const modules = query.data?.modules ?? []
  const module = modules.find((candidate) => candidate.moduleKey === moduleKey)
  if (!module || module.isActive) return children
  const home = resolveHomePath(
    session,
    (key) => modules.find((candidate) => candidate.moduleKey === key)?.isActive ?? true,
  )
  if (home) return <Navigate to={home} replace />
  const canManage = Boolean(session && hasPermission(session, 'settings.read'))
  return (
    <main className="authorization-state">
      <div>
        <House aria-hidden="true" />
        <span className="auth-page__eyebrow">Aucun accueil disponible</span>
        <h1>Aucun accueil n’est actif pour votre compte.</h1>
        <p>
          Vue d’ensemble, Vue d’ensemble-2, Vue d’ensemble-1 et le Centre des opérations sont désactivés dans
          Paramètres › Modules, ou réservés à d’autres rôles.
        </p>
        {canManage ? (
          <div className="authorization-state__actions">
            <Link to="/settings?tab=modules">Ouvrir Paramètres › Modules</Link>
          </div>
        ) : null}
      </div>
    </main>
  )
}
